import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { resetUserState, selectUser } from 'app/store/userSlice';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { logoutUser } from 'app/store/authSlice';
import { apiSlice } from '@/app/api/apiSlice';
import { openDialog } from 'app/store/fuse/dialogSlice';
import RingCentralAccountModal from 'app/shared-components/Modals/RingCentralAccountModal/RingCentralAccountModal';

function UserMenu() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [userMenu, setUserMenu] = useState(null);
  const userMenuClick = (event) => {
    setUserMenu(event.currentTarget);
  };

  const userMenuClose = () => {
    setUserMenu(null);
  };

  const ringCentralLogin = async (clientId, userId) => {
    window.location.href =
      import.meta.env.VITE_API_URL + '/auth/ringcentral?userId=' + userId + '&clientId=' + clientId;
  };

  const handleRingCentralClick = () => {
    userMenuClose();
    dispatch(
      openDialog({
        children: <RingCentralAccountModal userId={user.id} onAccountSelect={ringCentralLogin} />,
      }),
    );
  };

  const outlookLogin = () => {
    window.location.href = import.meta.env.VITE_API_URL + '/auth/outlook';
  };

  const handleSignOut = () => {
    dispatch(logoutUser());
    dispatch(resetUserState());
    dispatch(apiSlice.util.resetApiState());
    userMenuClose();
  };

  return (
    <>
      <Button
        className='min-h-40 min-w-40 px-0 md:px-16 py-0 md:py-6'
        onClick={userMenuClick}
        color='inherit'
      >
        <div className='hidden md:flex flex-col mx-4 items-end'>
          <Typography component='span' className='font-semibold flex'>
            {user?.name}
          </Typography>
          <Typography className='text-11 font-medium capitalize' color='text.secondary'>
            {user?.role?.name}
            {(!user.role || (Array.isArray(user.role) && user.role.length === 0)) && 'Guest'}
          </Typography>
        </div>

        {user.photoURL ? (
          <Avatar className='md:mx-4' alt='user photo' src={user.photoURL} />
        ) : (
          <Avatar className='md:mx-4'>{user.name ? user.name[0] : ''}</Avatar>
        )}
      </Button>

      <Popover
        open={Boolean(userMenu)}
        anchorEl={userMenu}
        onClose={userMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{
          paper: 'py-8',
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: isDarkMode ? '#1e293b' : undefined,
            color: isDarkMode ? '#f1f5f9' : undefined,
          },
          '& .MuiMenuItem-root': {
            color: isDarkMode ? '#f1f5f9' : undefined,
          },
          '& .MuiListItemIcon-root': {
            color: isDarkMode ? '#94a3b8' : undefined,
          },
        }}
      >
        {!user.role?.name || user.role?.length === 0 ? (
          <>
            <MenuItem component={Link} to='/sign-in' role='button'>
              <ListItemIcon className='min-w-40'>
                <FuseSvgIcon>heroicons-outline:lock-closed</FuseSvgIcon>
              </ListItemIcon>
              <ListItemText primary='Sign In' />
            </MenuItem>
            <MenuItem component={Link} to='/sign-up' role='button'>
              <ListItemIcon className='min-w-40'>
                <FuseSvgIcon>heroicons-outline:user-add </FuseSvgIcon>
              </ListItemIcon>
              <ListItemText primary='Sign up' />
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={handleRingCentralClick} role='button'>
              <ListItemIcon className='min-w-40'>
                <FuseSvgIcon>heroicons-outline:login</FuseSvgIcon>
              </ListItemIcon>
              <ListItemText primary='Connect to RingCentral' />
            </MenuItem>
            <MenuItem component={NavLink} onClick={outlookLogin}>
              <ListItemIcon className='min-w-40'>
                <FuseSvgIcon>heroicons-outline:inbox-in</FuseSvgIcon>
              </ListItemIcon>
              <ListItemText component='button' primary='Connect to Outlook' />
            </MenuItem>
            <MenuItem component={NavLink} to='/sign-out' onClick={handleSignOut}>
              <ListItemIcon className='min-w-40'>
                <FuseSvgIcon>heroicons-outline:logout</FuseSvgIcon>
              </ListItemIcon>
              <ListItemText primary='Sign out' />
            </MenuItem>
          </>
        )}
      </Popover>
    </>
  );
}

export default UserMenu;
